import { useState } from "react";
import { Landmark, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { submitLenderEstimate } from "@/lib/api";
import { track } from "@/lib/analytics";

/**
 * Bottom-of-funnel CTA on the Closing Cost Calculator. Captures a lender-
 * estimate request and routes it to a partner lender per ZIP. Pure capture,
 * no AI calls, no streaming — cost-safe.
 */
export default function LenderEstimateForm({
    homePrice,
    zipCode,
    loanType,
    toolSessionId,
    estimateSummary,
}) {
    const [email, setEmail] = useState("");
    const [firstName, setFirstName] = useState("");
    const [phone, setPhone] = useState("");
    const [zip, setZip] = useState(zipCode || "");
    const [targetClose, setTargetClose] = useState("");
    const [optIn, setOptIn] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        if (!email.trim() || !zip.trim() || !homePrice) return;
        setSubmitting(true);
        try {
            await submitLenderEstimate({
                email: email.trim().toLowerCase(),
                first_name: firstName.trim() || null,
                phone: phone.trim() || null,
                zip_code: zip.trim(),
                home_price: Number(homePrice) || 0,
                loan_type: loanType || "conventional",
                target_close_date: targetClose.trim() || null,
                marketing_opt_in: optIn,
                tool_session_id: toolSessionId || null,
                estimate_summary: estimateSummary || null,
            });
            track("lender_estimate_submitted", {
                loan_type: loanType,
                home_price_band:
                    homePrice >= 750000
                        ? "750k+"
                        : homePrice >= 500000
                        ? "500-750k"
                        : homePrice >= 300000
                        ? "300-500k"
                        : "under_300k",
                zip_present: !!zip,
                opted_in: optIn,
            });
            setSent(true);
            toast.success("Sent! A lender will reach out shortly.");
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                err?.response?.data?.message ||
                "We couldn't submit your request. Please try again.";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    if (sent) {
        return (
            <div
                className="bl-card p-5 sm:p-6 flex items-start gap-3"
                data-testid="lender-estimate-sent"
            >
                <CheckCircle2 className="h-5 w-5 text-bl-primary flex-shrink-0 mt-0.5" />
                <div>
                    <h3 className="font-semibold text-bl text-sm">
                        Your lender estimate request was received.
                    </h3>
                    <p className="text-sm text-bl-muted mt-1">
                        A vetted lender for ZIP {zip} will reach out shortly
                        with an itemized Loan Estimate.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <form
            onSubmit={onSubmit}
            className="bl-card p-5 sm:p-6 space-y-4"
            data-testid="lender-estimate-form"
        >
            <div className="flex items-start gap-3">
                <Landmark className="h-5 w-5 text-bl-primary flex-shrink-0 mt-0.5" />
                <div>
                    <h3 className="font-semibold text-bl text-base">
                        Get my exact closing costs
                    </h3>
                    <p className="text-sm text-bl-muted mt-1">
                        We'll route your numbers to a vetted lender for your
                        ZIP. They'll send an itemized Loan Estimate within
                        1–2 business days. Free and no obligation.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                    type="text"
                    placeholder="First name (optional)"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="bl-input"
                    data-testid="lender-first-name"
                />
                <input
                    type="email"
                    required
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bl-input"
                    data-testid="lender-email"
                />
                <input
                    type="tel"
                    placeholder="Phone (optional)"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="bl-input"
                    data-testid="lender-phone"
                />
                <input
                    type="text"
                    required
                    maxLength={5}
                    placeholder="ZIP"
                    value={zip}
                    onChange={(e) => setZip(e.target.value)}
                    className="bl-input"
                    data-testid="lender-zip"
                />
                <input
                    type="text"
                    placeholder="Target close date (optional, YYYY-MM-DD)"
                    value={targetClose}
                    onChange={(e) => setTargetClose(e.target.value)}
                    className="bl-input sm:col-span-2"
                    data-testid="lender-target-close"
                />
            </div>

            <label className="flex items-start gap-2 text-sm text-bl-muted">
                <input
                    type="checkbox"
                    checked={optIn}
                    onChange={(e) => setOptIn(e.target.checked)}
                    data-testid="lender-opt-in"
                    className="mt-1"
                />
                <span>
                    Send me strategic homebuyer tips and closing-cost
                    breakdowns occasionally. You can unsubscribe anytime.
                </span>
            </label>

            <button
                type="submit"
                disabled={submitting || !email.trim() || !zip.trim() || !homePrice}
                className="bl-btn-primary w-full sm:w-auto justify-center gap-2 disabled:opacity-50"
                data-testid="lender-submit"
            >
                {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                    <Landmark className="h-4 w-4" />
                )}
                {submitting ? "Sending…" : "Get my exact closing costs"}
            </button>

            <p className="text-xs text-bl-muted">
                We only share the info you provide with one matched lender for
                your ZIP. Buyer's Leverage is not a lender.
            </p>
        </form>
    );
}
